import { useState } from "react";
import PropTypes from "prop-types";

import Icon from "./Icon";
import T from "./translations/Translations";

import { DASH_LINKS } from "../const/AppCardLinks";

/**
 * Display a selectable list of student pictures
 * @name StudentCarousel
 * @param {bool} addAllowed
 * @param {func} setExpand
 * @param {func} setSelectedStudent
 * @param {[]} studentImageList
 * @param {[]} studentList
 * @returns {JSX.Element}
 */
const StudentCarousel = ({ addAllowed, setExpand, setSelectedStudent, studentImageList, studentList }) => {
    const [index, setIndex] = useState(0);

    const tAdd = T({ key: "Global_Add_New_Student" });

    /**
     * Direct the user to the OE home page
     * @name handleAdd
     */
    const handleAdd = () => {
        window.location.href = `${DASH_LINKS.open_enrollment}/home`;
    };

    /**
     * Select the next student in the list or start from beginning if at end
     * @name handleNext
     */
    const handleNext = () => {
        if (index < studentList?.length - 1) {
            const next = index + 1;
            setIndex(next);
            setSelectedStudent(studentList[next]);
        } else {
            setIndex(0);
            setSelectedStudent(studentList[0]);
        }
        setExpand(false);
    };

    /**
     * Select the previous student in the list or cycle to the end if at beginning
     * @name handlePrev
     */
    const handlePrev = () => {
        if (index > 0) {
            const prev = index - 1;
            setIndex(prev);
            setSelectedStudent(studentList[prev]);
        } else {
            setIndex(studentList.length - 1);
            setSelectedStudent(studentList[studentList.length - 1]);
        }
        setExpand(false);
    };

    /**
     * Set the selected student
     * @name handleSelect
     * @param {int} selectedIndex
     */
    const handleSelect = (selectedIndex) => {
        setIndex(selectedIndex);
        setSelectedStudent(studentList[selectedIndex]);
        setExpand(false);
    };

    return (
        <div className="d-flex align-items-center justify-content-center">
            {studentList?.length > 1 && (
                <button className="carousel-button" onClick={handlePrev}>
                    <Icon fill="#0669B3" height={30} iconName="LEFT_ARROW" width={30} />
                </button>
            )}
            <div className="d-flex">
                <div className="student-icon-container">
                    {studentList?.map((student, i) => {
                        const studentImage = studentImageList?.find(
                            (obj) => parseInt(obj.studentNumber, 10) === parseInt(student.studentNumber, 10)
                        );
                        const uniqueKey = `carousel-ic-${i}`;
                        if (studentImage) {
                            return (
                                <div
                                    className="rounded-2xl mx-1 mb-1"
                                    key={uniqueKey}
                                    onClick={() => handleSelect(i)}
                                    role="button"
                                    style={{
                                        alignItems: "center",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "90px",
                                        margin: "0 5px 0 5px",
                                        objectFit: "cover",
                                        opacity: i === index ? "1" : "0.5",
                                        transform: i === index ? "scale(1.05)" : "scale(1)",
                                        transition: "all 0.5s ease-in-out",
                                        width: "70px"
                                    }}
                                    tabIndex={0}
                                >
                                    <img
                                        alt={`${student.firstName} ${student.lastName}`}
                                        key={uniqueKey}
                                        src={studentImage.imageUrl}
                                        style={{
                                            borderRadius: "30px",
                                            height: "60px",
                                            objectFit: "cover",
                                            opacity: i === index ? "1" : "0.5",
                                            transform: i === index ? "scale(1.05)" : "scale(1)",
                                            transition: "all 0.5s ease-in-out",
                                            width: "60px"
                                        }}
                                    />
                                    <span className="carousel-name">{student.firstName}</span>
                                    {i === index && <div className="blue-bar">&nbsp;</div>}
                                </div>
                            );
                        }
                        return (
                            <div
                                className="rounded-2xl mx-1 mb-1"
                                key={uniqueKey}
                                onClick={() => handleSelect(i)}
                                role="button"
                                style={{
                                    alignItems: "center",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "90px",
                                    margin: "0 5px 0 5px",
                                    objectFit: "cover",
                                    opacity: i === index ? "1" : "0.5",
                                    transform: i === index ? "scale(1.05)" : "scale(1)",
                                    transition: "all 0.5s ease-in-out",
                                    width: "70px"
                                }}
                                tabIndex={0}
                            >
                                <div>
                                    <Icon
                                        fill="#19608F"
                                        height={60}
                                        iconName="NO_STUDENT_IMAGE"
                                        key={uniqueKey}
                                        style={{
                                            transform: i === index ? "scale(1.05)" : "scale(1)",
                                            transition: "all 0.5s ease-in-out"
                                        }}
                                        width={60}
                                    />
                                </div>
                                <span className="carousel-name">{student.firstName}</span>
                                {i === index && <div className="blue-bar">&nbsp;</div>}
                            </div>
                        );
                    })}
                </div>
                {addAllowed && (
                    <div className="icon-button mb-3" onClick={handleAdd} role="button" tabIndex={0}>
                        <div>
                            <Icon fill="#707070" height={60} iconName="ADD" width={60} />
                        </div>
                        <span className="carousel-name">{tAdd}</span>
                    </div>
                )}
            </div>
            {studentList?.length > 1 && (
                <button className="carousel-button" onClick={handleNext}>
                    <Icon fill="#0669B3" height={30} iconName="RIGHT_ARROW" width={30} />
                </button>
            )}
        </div>
    );
};

StudentCarousel.propTypes = {
    addAllowed: PropTypes.bool.isRequired,
    setExpand: PropTypes.func.isRequired,
    setSelectedStudent: PropTypes.func.isRequired,
    studentImageList: PropTypes.instanceOf(Array),
    studentList: PropTypes.instanceOf(Array)
};

StudentCarousel.defaultProps = {
    studentImageList: [],
    studentList: []
};

export default StudentCarousel;
